import { useQuery } from '@tanstack/react-query';
import { metadataAPI } from '../api/enhancedApiService';

/**
 * Custom hook to fetch columns data for a specific table
 * Uses tiered metadata system (UnifiedMetadataService) for proper data types and profiling data
 * @param {string} connectionId - The connection ID
 * @param {string} tableName - The table name to fetch columns for
 * @param {Object} options - Additional options for the query
 * @returns {Object} Query result object
 */
export const useTableColumns = (connectionId, tableName, options = {}) => {
  const {
    enabled = !!(connectionId && tableName),
    refetchInterval = false,
    schema = null,
    ...queryOptions
  } = options;

  return useQuery({
    queryKey: ['table-columns-tiered', connectionId, tableName, schema],
    queryFn: ({ signal }) => {
      // Use tier-aware metadata API for proper data_type and profiling data
      return metadataAPI.getTableColumnsTiered(
        connectionId,
        tableName,
        schema,
        { forceFresh: false, signal }
      );
    },
    enabled: enabled,
    refetchInterval: refetchInterval,
    ...queryOptions,
    select: (data) => {
      // Handle tiered metadata API response structure
      let columns = [];
      if (data?.columns) {
        columns = data.columns;
      } else if (Array.isArray(data)) {
        columns = data;
      }

      if (columns.length === 0) {
        return [];
      }

      // Transform tiered metadata format to component-expected format
      return columns.map(col => ({
        name: col.name,
        type: col.data_type || col.type || 'unknown',
        nullable: col.is_nullable !== undefined ? col.is_nullable : col.nullable,
        ...col,
        data_type: col.data_type || col.type,
        is_nullable: col.is_nullable !== undefined ? col.is_nullable : col.nullable,
        distinct_count: col.distinct_count || 0,
        null_count: col.null_count || 0,
        min_value: col.min_value,
        max_value: col.max_value
      }));
    },
    // Don't refetch on window focus for column schema data
    refetchOnWindowFocus: false,
    // Keep the data for 1 hour since schema doesn't change often
    gcTime: 60 * 60 * 1000,
    // Consider it stale after 10 minutes
    staleTime: 10 * 60 * 1000,
  });
};